package com.evoapps.evoshift.navigation

import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.navigation.NavDestination
import androidx.navigation.NavDestination.Companion.hasRoute
import com.evoapps.evoshift.Route
import kotlin.reflect.KClass

/**
 * Every route of the app, for reading a back stack as [Route] classes (type-safe destinations
 * only know their class). A test checks that none is missing.
 */
val allRoutes: List<KClass<out Route>> = listOf(
    Route.Login::class, Route.Dashboard::class, Route.Profile::class, Route.CreateWorkplace::class,
    Route.CreateRoster::class, Route.Team::class, Route.RosterManagement::class, Route.Availability::class,
    Route.Schedule::class, Route.AvailabilityForm::class, Route.WorkplaceSettings::class,
    Route.SkeletonEditor::class, Route.RosterDetail::class, Route.EmployeeForm::class,
    Route.RosterSkeleton::class, Route.JoinTeam::class, Route.WeeklyTemplates::class,
    Route.RecurringCosts::class, Route.Onboarding::class, Route.Financials::class, Route.Absences::class,
    Route.Notifications::class, Route.Requests::class, Route.DailyRecords::class, Route.Payroll::class,
    Route.Paywall::class, Route.Subscription::class, Route.More::class, Route.Recommend::class, Route.NotificationSettings::class,
    Route.RosterCapacity::class, Route.Checklists::class, Route.ShiftChecklist::class, Route.LeaveBalances::class,
    Route.Announcements::class, Route.StaffDocuments::class, Route.EmployeeTimeline::class, Route.Policies::class, Route.Tips::class, Route.SalesImport::class,
    Route.SharedCosts::class, Route.WorkplaceGroups::class, Route.GroupSettings::class, Route.Roles::class, Route.PersonAccess::class, Route.Hiring::class, Route.JobPostEditor::class, Route.Applicants::class,
    Route.Courses::class, Route.CourseEditor::class, Route.TakeCourse::class, Route.CourseProgress::class, Route.OhsTraining::class,
    Route.VoiceAction::class, Route.StaffImport::class, Route.RosterImport::class,
    Route.Statistics::class, Route.WorkHours::class
)

/** The [Route] class of this destination; null for the graph itself. */
fun NavDestination.routeClass(): KClass<out Route>? = allRoutes.firstOrNull { hasRoute(it) }

/** The back stack as route classes, oldest first, without the graph (see [selectedTab], [mustLeaveForHome]). */
fun List<NavBackStackEntry>.routeClasses(): List<KClass<out Route>> = mapNotNull { it.destination.routeClass() }

/** Does [move]: switches tabs keeping their stacks, or returns to a tab's root. */
fun NavController.go(move: TabMove) {
    when (move) {
        TabMove.Stay -> Unit
        is TabMove.PopToRoot ->
            if (!popBackStack(move.tab.rootClass, inclusive = false)) go(TabMove.Switch(move.tab, move.tab.root, false, false))
        is TabMove.Switch -> navigate(move.root) {
            // Home is the base of every tab: back from a tab returns there, and Home's own
            // details are not kept (its stack is the base, so it cannot be saved and restored).
            popUpTo(Route.Dashboard::class) { saveState = move.saveCurrent }
            launchSingleTop = true
            restoreState = move.restore
        }
    }
}

/** The tab of [tab] tapped in the bar or the rail. */
fun NavController.selectTab(tab: AppTab, tabs: List<AppTab>) {
    val stack = currentBackStack.value.routeClasses()
    go(tabMove(tab, selectedTab(stack, tabs), currentDestination?.routeClass()))
}

/**
 * Opens [route] from anywhere (a dashboard card, a notification, the More hub): a section with a
 * tab goes to its tab, anything else opens on top of the current one, once.
 */
fun NavController.open(route: Route, tabs: List<AppTab>) {
    val tab = tabs.firstOrNull { it.rootClass == route::class }
    if (tab != null) {
        val stack = currentBackStack.value.routeClasses()
        go(tabMove(tab, selectedTab(stack, tabs), currentDestination?.routeClass(), root = route))
    } else {
        navigate(route) { launchSingleTop = true }
    }
}

/**
 * Opens a screen reachable from every top bar (notifications, the profile): back to it when it
 * is already open below, so going round in circles does not pile them up.
 */
fun NavController.openOnce(route: Route) {
    if (!popBackStack(route::class, inclusive = false)) navigate(route) { launchSingleTop = true }
}

/** Opens [route] instead of everything (signing in or out, an invitation, the first run). */
fun NavController.openAsRoot(route: Route) {
    navigate(route) {
        popUpTo(graph.id) { inclusive = true }
        launchSingleTop = true
    }
}

/**
 * Signed in (or up) on the login screen: a waiting invitation opens now (it survived signing up),
 * else home, which starts the first run when needed.
 */
fun NavController.signedIn() {
    val link = DeepLinks.pending.value
    val target = routeAfterSignIn(link)
    if (target == link) DeepLinks.consume()
    openAsRoot(target)
}

/** Back to Home, the base of the signed-in app; Home alone when it is not there yet. */
fun NavController.goHome() {
    if (!popBackStack(Route.Dashboard::class, inclusive = false)) openAsRoot(Route.Dashboard)
}

/** Forgets the stacks the tabs keep (they show another workplace's or role's screens). */
fun NavController.forgetTabStacks() {
    AppTab.entries.filter { it != AppTab.HOME }.forEach { clearBackStack(it.root) }
}

/**
 * Another workplace: its screens (a month, its settings, its daily records) and the tabs' kept
 * stacks would show it under the new workplace's name, so they go; sections follow the session.
 * A month on top ([monthFollowingRoutes], SH-89) stays: it follows to the same month there.
 */
fun NavController.leaveWorkplaceScreens() {
    forgetTabStacks()
    while (currentDestination?.routeClass().let { it in workplaceRoutes && it !in monthFollowingRoutes } && previousBackStackEntry != null) {
        popBackStack()
    }
}
